import React, { useState, useCallback, useEffect, useMemo, memo } from 'react';
import { ChevronRight, MessageCircle, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useIsMobile } from '@/hooks/use-mobile';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import ChatComponent from '@/components/ChatComponent';
import { TranscriptionPanel } from '@/components/TranscriptionPanel';
import { useAuth } from '@/contexts/AuthContext';
import { ReportActions } from '@/components/ReportActions';
import ReportSection from '@/components/ReportSection';
import { AnalysisDates } from '@/components/AnalysisDates';
import { Analysis, DiffEntry, ApiTranscriptionData } from '@/types/sections';
import { useAutoReportGeneration } from '@/hooks/useAutoReportGeneration';
import { useConsultationTranscription } from '@/hooks/useConsultationTranscription';
import { uploadFileToStrapi } from '@/utils/fileUpload';
import { useToast } from '@/hooks/use-toast';
import { SendEmailModal } from '@/components/SendEmailModal';
import jsPDF from 'jspdf';
import ProblemModal from '@/components/ProblemModal';
import { useTranslation } from 'react-i18next';
import { GenerationStatusIndicator } from '@/components/GenerationStatusIndicator';

interface ReportViewerProps {
  analysis: Analysis;
  isLoading: boolean;
  isCreating?: boolean;
  isFileUploading?: boolean;
  onMenuClick?: () => void;
  isRecording?: boolean;
  currentAnalysisId?: number;
  transcriptions?: ApiTranscriptionData[];
  onAnalysisUpdate?: (updatedAnalysis: Analysis) => void;
}

const ReportViewerComponent = ({
  analysis,
  isLoading,
  isCreating = false,
  isFileUploading = false,
  onMenuClick,
  isRecording = false,
  currentAnalysisId,
  transcriptions = [],
  onAnalysisUpdate
}: ReportViewerProps) => {
  console.log('🔄 REPORT_VIEWER: Component mounting/re-rendering', {
    analysisId: analysis?.id,
    isRecording,
    currentAnalysisId,
    timestamp: new Date().toISOString()
  });

  const { t, i18n } = useTranslation();

  const [activeTab, setActiveTab] = useState<'report' | 'chat' | 'transcription'>('report');
  const [reportContent, setReportContent] = useState<{[key: string]: string}>({});
  const [reportDiffs, setReportDiffs] = useState<{[key: string]: DiffEntry[]}>({});
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [shouldResetTimer, setShouldResetTimer] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [problemModalOpen, setProblemModalOpen] = useState(false);
  const [localAnalysis, setLocalAnalysis] = useState(analysis);
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { toast } = useToast();

  // Mount/unmount logging
  useEffect(() => {
    console.log('🟢 REPORT_VIEWER: Component mounted', { analysisId: analysis?.id });
    return () => {
      console.log('🔴 REPORT_VIEWER: Component unmounting', { analysisId: analysis?.id });
    };
  }, [analysis?.id]);

  // Update local analysis when prop changes
  useEffect(() => {
    console.log('📝 REPORT_VIEWER: Analysis prop changed', { 
      oldId: localAnalysis?.id, 
      newId: analysis?.id,
      changed: localAnalysis?.id !== analysis?.id
    });
    setLocalAnalysis(analysis);
  }, [analysis, localAnalysis?.id]);

  // Memoize stable values to prevent unnecessary re-renders
  const isConsultationFinished = useMemo(() => 
    localAnalysis.attributes.finished || false, 
    [localAnalysis.attributes.finished]
  );
  
  const isPaused = useMemo(() => 
    !isRecording && currentAnalysisId === localAnalysis.id && !isConsultationFinished,
    [isRecording, currentAnalysisId, localAnalysis.id, isConsultationFinished]
  );

  // Memoize auto-generation hook props to prevent re-initialization
  const autoGenProps = useMemo(() => ({
    analysisId: analysis.id,
    isRecording: isRecording && currentAnalysisId === analysis.id,
    lang: i18n.language,
    jwt: localStorage.getItem('token') || '',
    userEmail: user?.email || ''
  }), [
    analysis.id,
    isRecording,
    currentAnalysisId,
    i18n.language,
    user?.email
  ]);

  // Inicializar reportDiffs con los diffs existentes del analysis
  useEffect(() => {
    if (analysis.attributes.diffs) {
      console.log('🔄 REPORT: Inicializando diffs desde analysis:', analysis.attributes.diffs);
      setReportDiffs(analysis.attributes.diffs);
    }
  }, [analysis.attributes.diffs]);

  // Hook de transcripción que recibe el tiempo actual
  const { transcriptions: liveTranscriptions } = useConsultationTranscription({
    analysisId: currentAnalysisId === analysis.id ? analysis.id : undefined,
    isRecording: isRecording && currentAnalysisId === analysis.id,
    currentTime: currentTime,
    onTranscriptionUpdate: (newTranscriptions) => {
      console.log('📝 REPORT: New transcriptions received:', newTranscriptions.length);
    }
  });

  // Always combine saved transcriptions with live ones when available
  const savedTranscriptions = analysis.attributes.transcription || [];
  const displayTranscriptions = useMemo(() => 
    currentAnalysisId === analysis.id && liveTranscriptions.length > 0 
      ? [...savedTranscriptions, ...liveTranscriptions] 
      : savedTranscriptions,
    [currentAnalysisId, analysis.id, liveTranscriptions, savedTranscriptions]
  );

  // Auto-generation hook - Use memoized props
  const {
    reportContent: autoGeneratedContent,
    setEditing,
    updateTranscription,
    editingState,
    isGeneratingTitle,
    isGeneratingReport,
    lastTitleGenerated,
    lastReportGenerated,
    titleError,
    reportError,
    progressiveSections,
    showProgressiveDisplay,
    onProgressiveDisplayComplete
  } = useAutoReportGeneration(autoGenProps);

  // Update transcription reference when transcriptions change
  useEffect(() => {
    if (isRecording && currentAnalysisId === analysis.id) {
      updateTranscription(displayTranscriptions);
    }
  }, [displayTranscriptions, isRecording, currentAnalysisId, analysis.id, updateTranscription]);

  // Handle time updates from timer - CRÍTICO: Actualizar currentTime con logs detallados
  const handleTimeUpdate = useCallback((formattedTime: string, totalSeconds: number) => {
    console.log('⏰ REPORT: Timer update received from TranscriptionTimer:', {
      formattedTime,
      totalSeconds,
      isRecording,
      currentAnalysisId,
      analysisId: analysis.id,
      isFinished: isConsultationFinished,
      isValidTime: totalSeconds > 0
    });
    
    // CRÍTICO: Validar que recibimos un tiempo válido
    if (totalSeconds <= 0) {
      console.error('🚨 REPORT: RECEIVED INVALID TIME FROM TIMER!', {
        formattedTime,
        totalSeconds,
        willNotUpdateCurrentTime: true
      });
      return; // No actualizar con tiempo inválido
    }
    
    setCurrentTime(totalSeconds);
    console.log('⏰ REPORT: currentTime state updated to:', totalSeconds);
  }, [isRecording, currentAnalysisId, analysis.id, isConsultationFinished]);

  // Reset timer only when recording stops AND we're not continuing
  useEffect(() => {
    if (!isRecording && currentAnalysisId === analysis.id) {
      console.log('🔄 REPORT: Recording stopped, preserving timer state. Current time:', currentTime);
    }
  }, [isRecording, currentAnalysisId, analysis.id, currentTime]);

  // Handle analysis update from parent and update local state
  const handleAnalysisUpdate = useCallback((updatedAnalysis: Analysis) => {
    console.log('📝 REPORT: Analysis updated, refreshing local state:', updatedAnalysis);
    setLocalAnalysis(updatedAnalysis);
    if (onAnalysisUpdate) {
      onAnalysisUpdate(updatedAnalysis);
    }
  }, [onAnalysisUpdate]);

  // Handle content change
  const handleContentChange = useCallback((sectionKey: string, content: string) => {
    setReportContent(prev => ({
      ...prev,
      [sectionKey]: content
    }));
  }, []);

  // Handle diff change
  const handleDiffChange = useCallback((sectionKey: string, diff: DiffEntry) => {
    console.log('🔄 REPORT: Recibiendo nuevo diff para sección:', sectionKey, diff);
    setReportDiffs(prev => {
      const updated = {
        ...prev,
        [sectionKey]: [...(prev[sectionKey] || []), diff]
      };
      console.log('🔄 REPORT: Diffs actualizados en estado local:', updated);
      return updated;
    });
  }, []);

  // Handle editing change
  const handleEditingChange = useCallback((sectionKey: string, isEditing: boolean) => {
    setEditing(sectionKey, isEditing);
  }, [setEditing]);

  // Define isAnyLoading by combining relevant loading states - include creation states
  const isAnyLoading = isLoading || isCreating || isFileUploading;

  if (isAnyLoading) {
    return <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>;
  }

  if (!analysis) {
    return <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">No se pudo cargar el análisis</p>
      </div>;
  }

  // ... keep existing code (handleDownloadPDF function)

  const handleDownloadPDF = async () => {
    console.log('Download PDF clicked');
    
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      pdf.setFontSize(18);
      pdf.setFont(undefined, 'bold');
      const title = localAnalysis.attributes.patient_name || 'Reporte Médico';
      pdf.text(title, margin, yPosition);
      yPosition += 15;

      pdf.setFontSize(12);
      pdf.setFont(undefined, 'normal');
      const date = new Date(localAnalysis.attributes.createdAt).toLocaleDateString('es-ES');
      pdf.text(`Fecha: ${date}`, margin, yPosition);
      yPosition += 20;

      const reportSections = localAnalysis.attributes.template?.data?.attributes?.sections?.filter(
        section => section.includeInReport
      ).sort((a, b) => a.order - b.order) || [];

      reportSections.forEach((section) => {
        const sectionContent = reportContent[section.key] || 
                             autoGeneratedContent[section.key] ||
                             localAnalysis.attributes.iaReport?.[section.key] || 
                             '';
        
        // Obtener los diffs existentes para esta sección desde reportDiffs o analysis
        const existingDiffs = reportDiffs[section.key] || localAnalysis.attributes.diffs?.[section.key] || [];
        
        if (sectionContent && sectionContent !== 'registrar') {
          pdf.setFontSize(14);
          pdf.setFont(undefined, 'bold');
          
          if (yPosition > pdf.internal.pageSize.getHeight() - 40) {
            pdf.addPage();
            yPosition = margin;
          }
          
          pdf.text(section.title, margin, yPosition);
          yPosition += 10;

          pdf.setFontSize(10);
          pdf.setFont(undefined, 'normal');
          
          const cleanContent = sectionContent.replace(/<[^>]*>/g, '').trim();
          
          const lines = pdf.splitTextToSize(cleanContent, maxWidth);
          
          lines.forEach((line: string) => {
            if (yPosition > pdf.internal.pageSize.getHeight() - 20) {
              pdf.addPage();
              yPosition = margin;
            }
            
            pdf.text(line, margin, yPosition);
            yPosition += 6;
          });
          
          yPosition += 10;
        }
      });

      if (displayTranscriptions.length > 0) {
        if (yPosition > pdf.internal.pageSize.getHeight() - 60) {
          pdf.addPage();
          yPosition = margin;
        } else {
          yPosition += 10;
        }

        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        pdf.text('Transcripción de la Consulta', margin, yPosition);
        yPosition += 15;

        displayTranscriptions.forEach((transcription) => {
          if (yPosition > pdf.internal.pageSize.getHeight() - 30) {
            pdf.addPage();
            yPosition = margin;
          }

          pdf.setFontSize(9);
          pdf.setFont(undefined, 'bold');
          const speakerLine = `${transcription.speakerType} (${transcription.recordTime}):`;
          pdf.text(speakerLine, margin, yPosition);
          yPosition += 8;

          pdf.setFontSize(9);
          pdf.setFont(undefined, 'normal');
          const transcriptLines = pdf.splitTextToSize(transcription.transcript, maxWidth);
          
          transcriptLines.forEach((line: string) => {
            if (yPosition > pdf.internal.pageSize.getHeight() - 15) {
              pdf.addPage();
              yPosition = margin;
            }
            
            pdf.text(line, margin, yPosition);
            yPosition += 5;
          });
          
          yPosition += 8;
        });
      }

      const filename = `reporte_${localAnalysis.attributes.patient_name || 'medico'}_${date.replace(/\//g, '-')}.pdf`;
      
      const pdfBlob = pdf.output('blob');
      
      toast({
        title: t('generating_pdf'),
        description: t('pdf_generating_loading'),
      });

      const uploadedFileId = await uploadFileToStrapi(pdfBlob, filename);
      
      if (uploadedFileId) {
        pdf.save(filename);
        
        toast({
          title: t('pdf_generated_successfully'),
          description: t('pdf_generated_uploaded'),
        });
        
        console.log('PDF uploaded to server with ID:', uploadedFileId);
      } else {
        pdf.save(filename);
        
        toast({
          title: t('pdf_downloaded'),
          description: t('pdf_downloaded_error_upload'),
          variant: 'destructive'
        });
      }
      
    } catch (error) {
      console.error('Error generating/uploading PDF:', error);
      toast({
        title: t('error'),
        description: t('error_generating_uploading_pdf'),
        variant: 'destructive'
      });
    }
  };

  const handleSendEmail = () => {
    console.log('Send email clicked');
    setEmailModalOpen(true);
  };

  const handleProblemClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setProblemModalOpen(true);
  };

  const handleFeedbackClick = () => {
    window.open('https://docs.google.com/forms/d/e/1FAIpQLSd3PMV_vWMu5QtngO4vrMfBMXAUMvaGLo-PxITT5Ts_esuCMw/viewform', '_blank');
  };

  // Memoize report sections to prevent unnecessary re-calculations
  const reportSections = useMemo(() => 
    localAnalysis.attributes.template?.data?.attributes?.sections?.filter(
      section => section.includeInReport
    ).sort((a, b) => a.order - b.order) || [],
    [localAnalysis.attributes.template?.data?.attributes?.sections]
  );

  const reportContentComponent = (
    <div className="h-full">
      <ScrollArea className="h-full">
        <div className="p-6 space-y-6 max-w-none">
          <ReportActions 
            onDownloadPDF={handleDownloadPDF}
            onSendEmail={handleSendEmail}
          />
          
          {/* Add generation status indicator with progressive sections */}
          {(isRecording && currentAnalysisId === localAnalysis.id) && (
            <GenerationStatusIndicator
              isGeneratingTitle={isGeneratingTitle}
              isGeneratingReport={isGeneratingReport}
              lastTitleGenerated={lastTitleGenerated}
              lastReportGenerated={lastReportGenerated}
              titleError={titleError}
              reportError={reportError}
              progressiveSections={progressiveSections}
              showProgressiveDisplay={showProgressiveDisplay}
              onProgressiveDisplayComplete={onProgressiveDisplayComplete}
            />
          )}
          
          {reportSections.length > 0 ? (
            reportSections.map((section) => {
              const sectionContent = reportContent[section.key] || 
                                   autoGeneratedContent[section.key] ||
                                   localAnalysis.attributes.iaReport?.[section.key] || 
                                   '';
              
              // Obtener los diffs existentes para esta sección desde reportDiffs o analysis
              const existingDiffs = reportDiffs[section.key] || localAnalysis.attributes.diffs?.[section.key] || [];
              
              console.log('🔄 REPORT: Renderizando sección:', section.key, 'con diffs:', existingDiffs);
              
              return (
                <ReportSection
                  key={section.id}
                  section={section}
                  content={sectionContent}
                  onContentChange={handleContentChange}
                  onDiffChange={handleDiffChange}
                  onEditingChange={handleEditingChange}
                  analysisId={localAnalysis.id}
                  isListening={isRecording && currentAnalysisId === localAnalysis.id}
                  existingDiffs={existingDiffs}
                />
              );
            })
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No hay secciones configuradas para este reporte.</p>
            </div>
          )}

          {/* Add the AnalysisDates component with isPaused prop */}
          <AnalysisDates 
            analysis={localAnalysis} 
            onAnalysisUpdate={handleAnalysisUpdate}
            isRecording={isRecording && currentAnalysisId === localAnalysis.id}
            isPaused={isPaused}
          />

          <div className="pt-8 pb-8 text-center">
            <button 
              type="button"
              onClick={handleProblemClick}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer bg-transparent border-none"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              <span>Reportar un problema</span>
            </button>
          </div>
        </div>
      </ScrollArea>
    </div>
  );

  // Helper function to check if current device is tablet
  const isTablet = () => {
    return window.innerWidth >= 768 && window.innerWidth < 1280;
  };

  const token = localStorage.getItem('token') || '';

  return <div className="flex h-screen bg-background">
      {/* Solo mostrar la barra superior si hay un análisis cargado y no está creando/cargando */}
      {analysis && !isAnyLoading && <div className="fixed top-0 left-64 right-0 z-50 bg-gray-900 text-white p-4 flex">
          <div className="flex-1 flex items-center justify-between xl:mr-80">
            <div className="flex space-x-2">
              <Button variant={activeTab === 'report' ? 'default' : 'outline'} size="sm" onClick={() => setActiveTab('report')} className={`
                  ${activeTab === 'report' ? 'bg-white text-black hover:bg-white hover:text-black' : 'bg-transparent border-gray-500 text-white hover:bg-transparent hover:text-white hover:border-gray-500'}
                `}>
                {t('report')}
              </Button>
              <Button variant={activeTab === 'chat' ? 'default' : 'outline'} size="sm" onClick={() => setActiveTab('chat')} className={`
                  ${activeTab === 'chat' ? 'bg-white text-black hover:bg-white hover:text-black' : 'bg-transparent border-gray-500 text-white hover:bg-transparent hover:text-white hover:border-gray-500'}
                `}>
                {t('chat')}
              </Button>
              {/* Agregar botón de transcripción en tablet */}
              {isTablet() && (
                <Button variant={activeTab === 'transcription' ? 'default' : 'outline'} size="sm" onClick={() => setActiveTab('transcription')} className={`
                    ${activeTab === 'transcription' ? 'bg-white text-black hover:bg-white hover:text-black' : 'bg-transparent border-gray-500 text-white hover:bg-transparent hover:text-white hover:border-gray-500'}
                  `}>
                  {t('transcription')}
                </Button>
              )}
            </div>
          </div>
          
          <div className="hidden xl:flex w-80 pl-4 items-center justify-between px-0">
            <div></div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleFeedbackClick}
              className="bg-transparent border-gray-500 text-white hover:bg-transparent hover:text-white hover:border-gray-500 flex items-center text-xs"
            >
              {t('give_feedback')}
              <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>}

      {/* Solo mostrar el header móvil si hay un análisis cargado y no está creando/cargando */}
      {isMobile && analysis && !isAnyLoading && <div className="fixed top-0 left-0 right-0 z-50 bg-gray-900 text-white">
          <div className="flex items-center justify-between p-4">
            <Button variant="ghost" size="sm" onClick={onMenuClick} className="text-white">
              <Menu className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold">clara.care</h1>
            <div className="w-8"></div>
          </div>
          
          <div className="flex border-t border-gray-700">
            <button onClick={() => setActiveTab('report')} className={`flex-1 px-4 py-3 text-sm font-medium ${activeTab === 'report' ? 'bg-white text-black' : 'text-gray-300 hover:text-white'}`}>
              {t('report')}
            </button>
            <button onClick={() => setActiveTab('chat')} className={`flex-1 px-4 py-3 text-sm font-medium ${activeTab === 'chat' ? 'bg-white text-black' : 'text-gray-300 hover:text-white'}`}>
              {t('chat')}
            </button>
            <button onClick={() => setActiveTab('transcription')} className={`flex-1 px-4 py-3 text-sm font-medium ${activeTab === 'transcription' ? 'bg-white text-black' : 'text-gray-300 hover:text-white'}`}>
              {t('transcription')}
            </button>
          </div>
        </div>}

      <div className={`flex-1 ${!isMobile ? 'xl:mr-80' : ''} ${analysis && !isAnyLoading ? isMobile ? 'pt-28' : 'pt-16' : 'pt-0'} bg-white`}>
        {activeTab === 'report' ? reportContentComponent : activeTab === 'chat' ? (
          <div className="flex-1 overflow-hidden">
            <ChatComponent 
              jwt={localStorage.getItem('token') || ''} 
              analysisId={analysis.id.toString()}
              lang={i18n.language}
            />
          </div>
        ) : activeTab === 'transcription' && (isMobile || isTablet()) ? (
          <div className="h-full bg-white">
            <TranscriptionPanel 
              transcriptions={displayTranscriptions} 
              showHeader={false} 
              showFeedback={false}
              autoScroll={isRecording && currentAnalysisId === analysis.id}
              isRecording={isRecording && currentAnalysisId === analysis.id}
              onTimeUpdate={handleTimeUpdate}
              isFinished={isConsultationFinished}
            />
          </div>
        ) : null}
      </div>

      {/* Solo mostrar el panel lateral si hay un análisis cargado y no está creando/cargando */}
      {!isMobile && !isTablet() && analysis && !isAnyLoading && <div className="hidden xl:flex w-80 bg-white fixed right-0 top-0 h-screen flex-col">
          <div className="flex-shrink-0 mt-16">
            <TranscriptionPanel 
              transcriptions={displayTranscriptions}
              autoScroll={isRecording && currentAnalysisId === analysis.id}
              isRecording={isRecording && currentAnalysisId === analysis.id}
              onTimeUpdate={handleTimeUpdate}
              shouldResetTimer={shouldResetTimer}
              isFinished={isConsultationFinished}
            />
          </div>
        </div>}
        
        <SendEmailModal
          isOpen={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
          selectedAnalysis={localAnalysis}
        />
        <ProblemModal
          isOpen={problemModalOpen}
          onClose={() => setProblemModalOpen(false)}
          user={user}
        />
    </div>;
};

// Use React.memo to prevent unnecessary re-renders
export const ReportViewer = memo(ReportViewerComponent, (prevProps, nextProps) => {
  // Custom comparison function to prevent re-renders when props haven't meaningfully changed
  const propsToCompare = [
    'analysis.id',
    'analysis.attributes.finished',
    'analysis.attributes.template',
    'isLoading',
    'isCreating',
    'isFileUploading',
    'isRecording',
    'currentAnalysisId'
  ];
  
  for (const prop of propsToCompare) {
    const keys = prop.split('.');
    const prevValue = keys.reduce((obj, key) => obj?.[key], prevProps);
    const nextValue = keys.reduce((obj, key) => obj?.[key], nextProps);
    
    if (prevValue !== nextValue) {
      console.log('🔄 REPORT_VIEWER: Re-rendering due to prop change:', prop, { prevValue, nextValue });
      return false;
    }
  }
  
  console.log('✅ REPORT_VIEWER: Props unchanged, preventing re-render');
  return true;
});
